import React, { useEffect, useState, useCallback, useMemo } from "react";
import Sidebar from "../../../components/Sidebar";
import {
  fetchDevices,
  removeDevice,
  fetchDeviceDetails,
  saveDeviceSettings,
} from "../../../controller/settingsController/activeDeviceController";
import { getStoredDeviceHeaders } from "../../../utils/apiClient";
import AppConstants from "../../../utils/appConstants";
import { useToast } from "../../../contexts/ToastContext";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import PermissionBrokerModelController from "../../../controller/model/PermissionBrokerModelController";
import "../../css/activeDevices.css";

export default function ActiveDevices() {
  const { success: showSuccess, error: showError } = useToast();
  const [user, setUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const currentDeviceId = getStoredDeviceHeaders()?.["device-id"] ?? null;
  const customerPermController = useMemo(
    () => new PermissionCustomerModelController(user),
    [user]
  );
  const brokerPermController = useMemo(
    () => new PermissionBrokerModelController(user),
    [user]
  );
  const userType = user?.TYPE ?? user?.type ?? user?.userType;
  const userIsBroker =
    userType === 2 || userType === 3 || userType === "BROKER" || userType === "IB";

  const brokerPermissionOptions = useMemo(
    () => [
      {
        key: PermissionBrokerModelController.PER__CAN_DEPOSIT_CLIENT,
        label: "Deposit Only Agent Clients",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_WITHDRAWAL_CLIENT,
        label: "Withdraw Only Agent Clients",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_ENABLE_TRADING_ACCOUNT__ACCOUNT,
        label: "Enable Account",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_ENABLE_TRADING_ACCOUNT__TRADING,
        label: "Enable Trading Account",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_SEND_NOTIFICATION,
        label: "Send Notification",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_VIEW_CLIENT,
        label: "View Client",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_VIEW_CLIENT_TRADING_HISTORY,
        label: "disabled View Client Trading History",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_COLLECT_CASHIN,
        label: "Collect Cashin",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_COLLECT_CASH_OUT,
        label: "Collect Cashout",
      },
      {
        key: PermissionBrokerModelController.PER__CAN_UPDATE_LEVERAGE,
        label: "Update Leverage",
      },
    ],
    []
  );

  const customerPermissionOptions = useMemo(
    () => [
      {
        key: PermissionCustomerModelController.PER__CAN_DEPOSIT_BY_CARD,
        label: "Deposit by Card",
      },
      { key: PermissionCustomerModelController.PER__CAN_CASH_OUT, label: "Cash Out" },
      {
        key: PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_MT5,
        label: "Internal Transfer from MT5 to MT5",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_USD_MT5,
        label: "Internal Transfer from USD to MT5",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_INTERNAL_TRANSFER_MT5_USD,
        label: "Internal Transfer from MT5 to USD",
      },
      { key: PermissionCustomerModelController.PER__CAN_EXCHANGE, label: "Exchange" },
      {
        key: PermissionCustomerModelController.PER__CAN_DEPOSIT_DEMO,
        label: "Deposit Demo Account",
      },
      { key: PermissionCustomerModelController.PER__CAN_CASH_IN, label: "Cash In" },
      {
        key: PermissionCustomerModelController.PER__MANAGEMENT_REAL_ACCOUNT,
        label: "Manage Real Account",
      },
      {
        key: PermissionCustomerModelController.PER__ADD_REAL_ACCOUNT,
        label: "Add Real Account",
      },
      {
        key: PermissionCustomerModelController.PER__MANAGEMENT_DEMO_ACCOUNT,
        label: "Manage Demo Account",
      },
      {
        key: PermissionCustomerModelController.PER__ADD_DEMO_ACCOUNT,
        label: "Add Demo Account",
      },
      {
        key: PermissionCustomerModelController.PER__SEND_MESSAGE,
        label: "Send Message",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_UPDATE_LEVERAGE,
        label: "Update Leverage",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_PENDING_REQUEST,
        label: "View Pending Request",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_HISTORY,
        label: "View History",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_ACCOUNT_HISTORY,
        label: "disabled View Account History",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_VIEW_TOTAL_VOLUME_ACCOUNTS,
        label: "disabled Total Volume Account",
      },
      {
        key: PermissionCustomerModelController.PER__CAN_DOWNLOAD_HISTORY_PDF,
        label: "disabled Download History Pdf",
      },
      {
        key: PermissionCustomerModelController.PER__DISABLE_COPY_TRADE,
        label: "Disable Copy Trade",
      },
      {
        key: PermissionCustomerModelController.PER__DISABLE_BECOME_MASTER,
        label: "Disable Become a Master",
      },
    ],
    []
  );

  const editableBrokerOptions = useMemo(
    () =>
      brokerPermissionOptions.filter((item) =>
        brokerPermController.permissionsList?.includes(item.key)
      ),
    [brokerPermController.permissionsList, brokerPermissionOptions]
  );

  const editableCustomerOptions = useMemo(
    () =>
      customerPermissionOptions.filter((item) =>
        customerPermController.permissionsList?.includes(item.key)
      ),
    [customerPermController.permissionsList, customerPermissionOptions]
  );

  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("userData");
      if (storedUser) setUser(JSON.parse(storedUser));
    } catch (_) {}
  }, []);

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchDevices();
      const code = Number(result?.code);
      if (code === AppConstants.responseOk) {
        setIsOwner(result.isOwner);
        setDevices(result.devices);
      } else {
        setDevices([]);
      }
    } catch {
      showError("Error", "Failed to load devices");
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const data = await removeDevice(deleteTarget);
      const code = Number(data?.CODE);
      if (code === AppConstants.responseOk) {
        showSuccess("Success", "Device removed successfully");
        await loadDevices();
      } else {
        showError("Error", "Failed to remove device");
      }
    } catch {
      showError("Error", "Failed to remove device");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const openEditModal = async (device) => {
    setEditTarget(device.ID);
    setEditLoading(true);
    setEditForm(null);
    try {
      const result = await fetchDeviceDetails(device.ID);
      if (Number(result?.code) === AppConstants.responseOk && result?.device) {
        const selectedDevice = result.device;
        const isCurrentDevice =
          String(selectedDevice.DEVICE_ID ?? device.DEVICE_ID) === String(currentDeviceId);
        const permissionObject =
          typeof selectedDevice.PERMISSION === "string"
            ? (() => {
                try {
                  return JSON.parse(selectedDevice.PERMISSION);
                } catch {
                  return {};
                }
              })()
            : selectedDevice.PERMISSION && typeof selectedDevice.PERMISSION === "object"
              ? selectedDevice.PERMISSION
              : {};
        const brokerDevicePermissions = PermissionBrokerModelController.fromDevice({
          PERMISSION: permissionObject,
        }).permissionsList;
        const customerDevicePermissions = PermissionCustomerModelController.fromDevice({
          PERMISSION: permissionObject,
        }).permissionsList;

        setEditForm({
          id: selectedDevice.ID ?? device.ID,
          permissionObject,
          brokerPermissions: Array.isArray(brokerDevicePermissions)
            ? [...brokerDevicePermissions]
            : [],
          customerPermissions: Array.isArray(customerDevicePermissions)
            ? [...customerDevicePermissions]
            : [],
          isOwner: selectedDevice.IS_OWNER === 1,
          isActive: selectedDevice.IS_ACTIVE === 1,
          receiveNotification: selectedDevice.RECEIVE_NOTIFICATION === 1,
          canEdit: result.isOwner === true,
          isCurrentDevice,
          name: selectedDevice.DEVICE_MODEL || device.DEVICE_MODEL || "Device",
        });
      } else {
        showError("Error", "Failed to load device details");
        setEditTarget(null);
      }
    } catch {
      showError("Error", "Failed to load device details");
      setEditTarget(null);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    if (savingEdit) return;
    setEditTarget(null);
    setEditLoading(false);
    setEditForm(null);
  };

  const updateEditField = (field, value) => {
    setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const toggleDevicePermission = (section, permissionKey, enabled) => {
    const targetField =
      section === "BROKER" ? "brokerPermissions" : "customerPermissions";
    setEditForm((prev) => {
      if (!prev) return prev;
      const currentList = Array.isArray(prev[targetField]) ? prev[targetField] : [];
      const hasPermission = currentList.includes(permissionKey);
      let nextList = currentList;
      if (enabled && !hasPermission) {
        nextList = [...currentList, permissionKey];
      }
      if (!enabled && hasPermission) {
        nextList = currentList.filter((item) => item !== permissionKey);
      }
      return { ...prev, [targetField]: nextList };
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm || savingEdit) return;
    setSavingEdit(true);
    try {
      const response = await saveDeviceSettings({
        DEVICE_ID: editForm.id,
        PERMISSION: JSON.stringify({
          ...(editForm.permissionObject || {}),
          BROKER: editForm.brokerPermissions || [],
          CUSTOMER: editForm.customerPermissions || [],
        }),
        IS_OWNER: !!editForm.isOwner,
        IS_ACTIVE: !!editForm.isActive,
        RECEIVE_NOTIFICATION: !!editForm.receiveNotification,
      });
      const code = Number(response?.CODE);
      if (code === AppConstants.responseOk) {
        showSuccess("Success", "Device updated successfully");
        closeEditModal();
        await loadDevices();
      } else {
        showError("Error", "Please try again");
      }
    } catch {
      showError("Error", "Please try again");
    } finally {
      setSavingEdit(false);
    }
  };

  const formatDate = (raw) => {
    if (!raw) return "-";
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return raw;
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return raw;
    }
  };

  return (
    <div className="active-devices-page">
      <Sidebar user={user} />
      <div className="active-devices-content">
        <h1 className="active-devices-title">Active Devices</h1>

        <div className="active-devices-container">
          {loading ? (
            <div className="active-devices-loading">
              <div className="active-devices-spinner" />
              <span>Loading devices...</span>
            </div>
          ) : devices.length === 0 ? (
            <div className="active-devices-empty">No devices found</div>
          ) : (
            devices.map((device) => {
              const isCurrent = String(device.DEVICE_ID) === String(currentDeviceId);
              const deviceIsOwner = device.IS_OWNER === 1;
              const deviceIsActive = device.IS_ACTIVE === 1;

              return (
                <div
                  key={device.ID}
                  className={`active-device-card${isCurrent ? " active-device-card--current" : ""}`}
                >
                  <div className="active-device-card-header">
                    <span className="active-device-card-name">
                      {device.DEVICE_MODEL || "-"}
                    </span>
                    {isCurrent && (
                      <span className="active-device-card-badge active-device-card-badge--current">
                        This Device
                      </span>
                    )}
                    {deviceIsOwner && (
                      <span className="active-device-card-badge active-device-card-badge--owner">
                        Owner
                      </span>
                    )}
                    {!deviceIsActive && (
                      <span className="active-device-card-badge active-device-card-badge--inactive">
                        Not Active
                      </span>
                    )}
                  </div>

                  <div className="active-device-card-details">
                    <div className="active-device-card-detail">
                      Device Type: <span>{device.OS || "-"}</span>
                    </div>
                    <div className="active-device-card-detail">
                      Start Date: <span>{formatDate(device.CREATED_AT)}</span>
                    </div>
                    <div className="active-device-card-detail">
                      Device ID: <span>{device.DEVICE_ID || "-"}</span>
                    </div>
                  </div>

                  {isOwner && (
                    <div className="active-device-card-actions">
                      <button
                        type="button"
                        className="active-device-card-btn active-device-card-btn--edit"
                        disabled={!isOwner}
                        onClick={() => openEditModal(device)}
                      >
                        <i className="fa-solid fa-pen" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="active-device-card-btn active-device-card-btn--delete"
                        disabled={isCurrent}
                        onClick={() => setDeleteTarget(device.ID)}
                      >
                        <i className="fa-solid fa-trash" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {deleteTarget !== null && (
        <div className="active-devices-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="active-devices-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="active-devices-dialog-title">Delete Device</h3>
            <p className="active-devices-dialog-text">
              Are you sure you want to remove this device? This action cannot be undone.
            </p>
            <div className="active-devices-dialog-actions">
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--cancel"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                No
              </button>
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--confirm"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editTarget !== null && (
        <div className="active-devices-overlay" onClick={closeEditModal}>
          <div className="active-devices-dialog active-devices-dialog--edit" onClick={(e) => e.stopPropagation()}>
            <h3 className="active-devices-dialog-title">Edit Device</h3>
            {editLoading || !editForm ? (
              <p className="active-devices-dialog-text">Loading device settings...</p>
            ) : (
              <div className="active-devices-dialog-body">
                <p className="active-devices-dialog-subtitle">{editForm.name}</p>
                <div className="active-device-edit-field">
                  <span>Is Owner</span>
                  <label className="active-device-switch">
                    <input
                      type="checkbox"
                      checked={editForm.isOwner}
                      disabled={!editForm.canEdit || editForm.isCurrentDevice || savingEdit}
                      onChange={(e) => updateEditField("isOwner", e.target.checked)}
                    />
                    <span className="active-device-switch-slider" />
                  </label>
                </div>
                <div className="active-device-edit-field">
                  <span>Is Active</span>
                  <label className="active-device-switch">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      disabled={!editForm.canEdit || editForm.isCurrentDevice || savingEdit}
                      onChange={(e) => updateEditField("isActive", e.target.checked)}
                    />
                    <span className="active-device-switch-slider" />
                  </label>
                </div>
                <div className="active-device-edit-field">
                  <span>Receive Notification</span>
                  <label className="active-device-switch">
                    <input
                      type="checkbox"
                      checked={editForm.receiveNotification}
                      disabled={!editForm.canEdit || savingEdit}
                      onChange={(e) => updateEditField("receiveNotification", e.target.checked)}
                    />
                    <span className="active-device-switch-slider" />
                  </label>
                </div>
                {editForm.isCurrentDevice && (
                  <p className="active-devices-dialog-text">
                    Owner and active status cannot be changed for the current device.
                  </p>
                )}
                {!editForm.canEdit && (
                  <p className="active-devices-dialog-text">You do not have permission to edit this device.</p>
                )}
                {userIsBroker && editableBrokerOptions.length > 0 && (
                  <div className="active-device-permissions-group">
                    <h4 className="active-device-permissions-title">Broker Permissions</h4>
                    {editableBrokerOptions.map((permission) => (
                      <div className="active-device-edit-field" key={`broker-${permission.key}`}>
                        <span>{permission.label}</span>
                        <label className="active-device-switch">
                          <input
                            type="checkbox"
                            checked={editForm.brokerPermissions?.includes(permission.key)}
                            disabled={!editForm.canEdit || savingEdit}
                            onChange={(e) =>
                              toggleDevicePermission("BROKER", permission.key, e.target.checked)
                            }
                          />
                          <span className="active-device-switch-slider" />
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {editableCustomerOptions.length > 0 && (
                  <div className="active-device-permissions-group">
                    <h4 className="active-device-permissions-title">Customer Permissions</h4>
                    {editableCustomerOptions.map((permission) => (
                      <div className="active-device-edit-field" key={`customer-${permission.key}`}>
                        <span>{permission.label}</span>
                        <label className="active-device-switch">
                          <input
                            type="checkbox"
                            checked={editForm.customerPermissions?.includes(permission.key)}
                            disabled={!editForm.canEdit || savingEdit}
                            onChange={(e) =>
                              toggleDevicePermission("CUSTOMER", permission.key, e.target.checked)
                            }
                          />
                          <span className="active-device-switch-slider" />
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="active-devices-dialog-actions">
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--cancel"
                onClick={closeEditModal}
                disabled={savingEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="active-devices-dialog-btn active-devices-dialog-btn--confirm"
                onClick={handleSaveEdit}
                disabled={editLoading || !editForm || savingEdit || !editForm.canEdit}
              >
                {savingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
